import SWSLogoBlack from '@assets/images/logo/sws-logo-black.svg';
import SWSLogo from '@assets/images/logo/sws-logo-updated.svg';
import DownloadAppStore from '@assets/images/icons/vendor/download-on-the-appstore.svg';
import DownloadGooglePlay from '@assets/images/icons/vendor/get-it-on-google-play.svg';
import DownloadChromeExtension from '@assets/images/icons/vendor/download-chrome-extension.svg';
import LinkedInIcon from '@assets/images/logo/linkedin.svg';
import XIcon from '@assets/images/logo/x.svg';
import YouTubeIcon from '@assets/images/logo/youtube-logo.svg';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import { RouterLink } from '@redux-first-router-link';
import {
  ROUTE_COMPANY,
  ROUTE_CREATE_CRITERIA,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_GIFT_CARD_PURCHASE,
  ROUTE_GIFT_CARD_REDEEM,
  ROUTE_INVESTING_IDEA_HOME,
  ROUTE_LOCALE_COMPANY,
  ROUTE_MARKET,
  ROUTE_MARKET_HOME,
  ROUTE_MARKET_HUB,
  ROUTE_MARKET_SECTOR,
  ROUTE_MARKET_SECTOR_SECONDARY,
  ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
  ROUTE_NARRATIVE_COMMUNITY,
  ROUTE_PLANS,
} from '@/constants/routes';
import { useLocation } from '@/hooks/useLocation';
import { cn } from '@simplywallst/ui-core/next';
import { useTranslation } from '@/hooks/useTranslation';
import useUser from '@/hooks/useUser';
import DocumentSchema from '@/components/DocumentSchema';
import type { Dataset } from 'schema-dts';

export function Footer() {
  const { data: userData } = useUser();
  const snpGlobalLogo = useCloudflareImage({
    src: 'https://cdn.prod.website-files.com/648009cb318551f1806b36b4/6487b1b44ebc33f108273875_sp-global-logo.png',
  });

  const snpGlobalLogoLight = useCloudflareImage({
    src: 'https://simplywall.st/static/images/sp-global-logo-1.png',
  });

  const currentYear = new Date().getFullYear();

  const location = useLocation();
  const showClearbit =
    location.type === ROUTE_COMPANY || location.type === ROUTE_LOCALE_COMPANY;

  const isPrintMode = location.query?.viewmode === 'print';

  const t = useTranslation();

  if (
    RUNTIME_ENV === 'native' ||
    // disabled in markets page temporarily
    [
      ROUTE_MARKET,
      ROUTE_MARKET_HOME,
      ROUTE_MARKET_SECTOR,
      ROUTE_MARKET_SECTOR_SECONDARY,
      ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
    ].includes(location.type)
  ) {
    return <></>;
  }

  const textColour = isPrintMode ? 'text-seethrough' : 'text-solid';

  return (
    <>
      <DocumentSchema<Dataset>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Dataset',
          name: t('page.schema.dataSet.name'),
          description: t('page.schema.dataSet.description'),
          creator: {
            '@type': 'Organization',
            name: 'Simply Wall St Pty Ltd',
            url: 'https://simplywall.st',
          },
          isBasedOn: {
            '@type': 'Dataset',
            name: 'S&P Global Market Intelligence',
            description: t('page.schema.dataSet.s&p.description'),
            provider: {
              '@type': 'Organization',
              name: 'S&P Global',
              url: 'https://www.spglobal.com',
            },
          },
          license:
            'https://www.spglobal.com/content/dam/spglobal/corporate/en/documents/secure/MI-FastTrack-Terms-and-Conditions-July-2023.pdf',
        }}
      />
      <footer data-cy-id="footer" className="flex items-center justify-center">
        <div className="mx-auto w-full max-w-screen-xl">
          <div className="grid grid-cols-1 gap-4 px-x2 pt-x8 md:grid-cols-3 md:px-x2 lg:px-x0">
            <div className="w-full md:col-span-1">
              <div className="max-w-[164px]">
                <div className="block">
                  {isPrintMode ? <SWSLogoBlack /> : <SWSLogo />}
                </div>
              </div>
              <address
                className={cn(
                  'mt-x2 font-base text-xs not-italic opacity-50',
                  textColour
                )}
              >
                Level 5, 320 Pitt Street, Sydney
              </address>
              <div
                className={cn(
                  'mt-x4 flex space-x-4',
                  isPrintMode ? 'hidden' : ''
                )}
              >
                <a
                  href="https://www.linkedin.com/company/simply-wall-st"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  aria-label="LinkedIn"
                >
                  <LinkedInIcon />
                </a>
                <a
                  href="https://www.youtube.com/c/Simplywallst"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  aria-label="YouTube"
                >
                  <YouTubeIcon />
                </a>
                <a
                  href="https://twitter.com/simplywallst"
                  rel="noopener noreferrer nofollow"
                  target="_blank"
                  aria-label="Twitter"
                >
                  <XIcon />
                </a>
              </div>
              <div
                className={cn(
                  'mt-x4 flex space-x-2',
                  isPrintMode ? 'hidden' : ''
                )}
              >
                <a
                  href="https://apps.apple.com/app/apple-store/id1075614972?pt=117976858&ct=website-footer&mt=8"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  aria-label="Download on the App Store"
                >
                  <DownloadAppStore />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.simplywallst.app&referrer=utm_source%3Dwebsite%26utm_medium%3Dfooter%26utm_content%3Dgoogle-play-logo"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  aria-label="Get it on Google Play"
                >
                  <DownloadGooglePlay />
                </a>
              </div>
              <div className={cn('mt-x1 flex', isPrintMode ? 'hidden' : '')}>
                <a
                  href="https://chromewebstore.google.com/detail/stock-analysis-overlay-si/fogdlgjnkolhbefbbpghbafdkhcdgdnk"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  aria-label="Download free extension on Chrome Web Store"
                >
                  <DownloadChromeExtension />
                </a>
              </div>
              <div className="mt-x8 pb-x4">
                <a
                  href="https://spglobal.com/market-intelligence/en"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  <img
                    src={isPrintMode ? snpGlobalLogoLight : snpGlobalLogo}
                    width="170"
                    alt="S&P Global Market Intelligence"
                    loading="lazy"
                  />
                </a>
                <p
                  className={cn(
                    'my-x2 font-base text-xs not-italic opacity-50',
                    textColour
                  )}
                >
                  Financial Data provided by S&P Global Market Intelligence LLC,
                  analysis provided by Simply Wall Street Pty Ltd. Copyright
                  &copy; {currentYear}, S&P Global Market Intelligence LLC. All
                  rights reserved.
                </p>
                <a
                  href="https://simplywall.st/analysis-and-financial-data-sources"
                  className={cn(
                    'my-x2 font-base text-xs no-underline',
                    textColour
                  )}
                >
                  {t('app.footer.links.viewDataSources')}
                </a>
              </div>
            </div>
            <div className="w-full md:col-span-2">
              <div
                className={cn(
                  'grid grid-cols-2 gap-4 md:grid-cols-4',
                  isPrintMode ? 'hidden' : ''
                )}
              >
                <div className="col-span-1 pt-x2">
                  <p className={cn('font-base text-xs opacity-50', textColour)}>
                    {t('app.footer.labels.markets')}
                  </p>
                  <ul className="mt-x4">
                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_MARKET, payload: { market: 'us' } }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        US: NYSE &amp; NASDAQ
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_MARKET, payload: { market: 'gb' } }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        UK: FTSE
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_MARKET, payload: { market: 'au' } }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        Australia: ASX
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_MARKET, payload: { market: 'in' } }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        India: NIFTY
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_MARKET, payload: { market: 'ca' } }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        Canada: TSX
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_MARKET, payload: { market: 'za' } }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        South Africa: JSE
                      </RouterLink>
                    </li>
                  </ul>
                </div>
                <div className="col-span-1 pt-x2">
                  <p className={cn('font-base text-xs opacity-50', textColour)}>
                    {t('app.footer.labels.investingIdeas')}
                  </p>
                  <ul className="mt-x4">
                    <li className="mt-x2">
                      <RouterLink
                        to={{
                          type: ROUTE_INVESTING_IDEA_HOME,
                          payload: {
                            id: 168,
                            name: 'undervalued-stocks-based-on-cash-flows',
                          },
                        }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.idealinks.undervalued')}
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{
                          type: ROUTE_INVESTING_IDEA_HOME,
                          payload: {
                            id: 146,
                            name: 'dividend-powerhouses-3-yield',
                          },
                        }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.idealinks.dividendPowerhouses')}
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{
                          type: ROUTE_INVESTING_IDEA_HOME,
                          payload: {
                            id: 171,
                            name: 'insider-trading',
                          },
                        }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.idealinks.insiderBuying')}
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{
                          type: ROUTE_INVESTING_IDEA_HOME,
                          payload: {
                            id: 215173,
                            name: 'nuclear-energy-stocks',
                          },
                        }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.idealinks.nuclearEnergy')}
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{
                          type: ROUTE_INVESTING_IDEA_HOME,
                          payload: {
                            id: 5620,
                            name: 'electric-autonomous-vehicle-stocks',
                          },
                        }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.idealinks.autonomousVehicle')}
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{
                          type: ROUTE_INVESTING_IDEA_HOME,
                          payload: {
                            id: 215169,
                            name: 'artificial-intelligence-ai-stocks',
                          },
                        }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.idealinks.artificialIntelligence')}
                      </RouterLink>
                    </li>
                  </ul>
                </div>
                <div className="col-span-1 pt-x2">
                  <p className={cn('font-base text-xs opacity-50', textColour)}>
                    {t('app.footer.labels.features')}
                  </p>
                  <ul className="mt-x4">
                    <li className="mt-x2">
                      <a
                        href="/features/portfolio"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.portfolioTracker')}
                      </a>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_CREATE_CRITERIA }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.screener')}
                      </RouterLink>
                    </li>

                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_DISCOVER_INVESTING_IDEAS }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.investingIdeas')}
                      </RouterLink>
                    </li>

                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_NARRATIVE_COMMUNITY }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.communityNarratives')}
                      </RouterLink>
                    </li>

                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_MARKET_HUB }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.marketInsights')}
                      </RouterLink>
                    </li>

                    <li className="mt-x2">
                      <a
                        href="https://support.simplywall.st/hc/en-us/articles/7894830045199-Changelog"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.whatsNew')}
                      </a>
                    </li>
                    <li className="mt-x2">
                      <a
                        href="https://simplywall.st/tools/dividend-calculator"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.dividendCalculator')}
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="col-span-1 pt-x2">
                  <p className={cn('font-base text-xs opacity-50', textColour)}>
                    Simply Wall St
                  </p>
                  <ul className="mt-x4">
                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_PLANS }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.plans')}
                      </RouterLink>
                    </li>
                    <li className="mt-x2">
                      <RouterLink
                        to={{ type: ROUTE_GIFT_CARD_PURCHASE }}
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.giftSubscription')}
                      </RouterLink>
                    </li>
                    {userData.loggedIn ? (
                      <li className="mt-x2">
                        <RouterLink
                          to={{ type: ROUTE_GIFT_CARD_REDEEM }}
                          className={cn(
                            'font-base text-xs no-underline',
                            textColour
                          )}
                        >
                          {t('app.footer.links.redeemGiftCard')}
                        </RouterLink>
                      </li>
                    ) : null}
                    <li className="mt-x2">
                      <a
                        href="/about"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.aboutUs')}
                      </a>
                    </li>
                    <li className="mt-x2">
                      <a
                        href="/our-people"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.ourPeople')}
                      </a>
                    </li>
                    <li className="mt-x2">
                      <a
                        href="https://support.simplywall.st/hc/en-us/requests/new"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.contactUs')}
                      </a>
                    </li>
                    <li className="mt-x2">
                      <a
                        href="/careers"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.careers')}
                      </a>
                    </li>
                  </ul>
                  <ul className="mt-x4">
                    <li className="mt-x2">
                      <a
                        href="https://support.simplywall.st/hc/en-us"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.help')}
                      </a>
                    </li>
                    <li className="mt-x2">
                      <a
                        href="https://learn.simplywall.st/"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.learn')}
                      </a>
                    </li>
                    <li className="mt-x2">
                      <a
                        href="/affiliate-program"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.affiliate')}
                      </a>
                    </li>
                    <li className="mt-x2">
                      <a
                        href="/business-plans"
                        className={cn(
                          'font-base text-xs no-underline',
                          textColour
                        )}
                      >
                        {t('app.footer.links.business')}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="pb-x8 pt-x6 md:pb-x4">
                <p className={cn('font-base text-xs opacity-50', textColour)}>
                  Simply Wall Street Pty Ltd (ACN 600 056 611), is a Corporate
                  Authorised Representative (Authorised Representative Number:
                  467183) of Sanlam Private Wealth Pty Ltd (AFSL No. 337927).
                  Any advice contained in this website is general advice only
                  and has been prepared without considering your objectives,
                  financial situation or needs. You should not rely on any
                  advice and/or information contained in this website and before
                  making any investment decision we recommend that you consider
                  whether it is appropriate for your situation and seek
                  appropriate financial, taxation and legal advice. Please read
                  our Financial Services Guide before deciding whether to obtain
                  financial services from us.
                </p>
                <p className={cn('font-base text-xs opacity-50', textColour)}>
                  &copy; {currentYear} Simply Wall Street Pty Ltd, US Design
                  Patent #29/544/281, Community and European Design Registration
                  #2845206
                </p>
                <div className="flex flex-col space-y-2 md:flex-row md:space-x-6 md:space-y-0">
                  <a
                    href="https://media.simplywall.st/site/financials/Financial_Services_Guide_-_Simply_Wall_St_V2.0_dated_20_Jan_23.pdf"
                    className={cn(
                      'font-base text-xs no-underline opacity-50',
                      textColour
                    )}
                  >
                    {t('app.footer.labels.featuresServicesGuide')}
                  </a>
                  <a
                    href="/terms-and-conditions"
                    className={cn(
                      'font-base text-xs no-underline opacity-50',
                      textColour
                    )}
                  >
                    {t('app.footer.links.terms')}
                  </a>
                  <a
                    href="/privacy-policy"
                    className={cn(
                      'font-base text-xs no-underline opacity-50',
                      textColour
                    )}
                  >
                    {t('app.footer.links.privacy')}
                  </a>
                  {showClearbit && (
                    <a
                      href="https://clearbit.com/"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className={cn(
                        'font-base text-xs no-underline opacity-50',
                        textColour
                      )}
                    >
                      {t('app.footer.links.clearbit')}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
